import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownOption } from "@cms/shared-library";
import { TranslateService } from '@ngx-translate/core';
import { ReCaptchaV3Service } from "ng-recaptcha-2";
import { Observable, Subscription, debounceTime, forkJoin, map, startWith } from 'rxjs';
import { ViewOptionsEnum } from 'src/app/enums/view-options.enum';
import { CourseSchedule } from 'src/app/models/course-schedule';
import { CourseSearchForm } from 'src/app/models/course-search-form.model';
import { TrainingProvider } from 'src/app/models/training-provider.model';
import { TrainingType } from 'src/app/models/training-type.model';
import { AddressService } from 'src/app/services/address/address.service';
import { CourseService } from 'src/app/services/course/course.service';
import { TrainingProgramService } from 'src/app/services/training-program/training-program.service';
import { TrainingProviderService } from 'src/app/services/training-provider/training-provider.service';
import { TrainingTypeService } from 'src/app/services/training-type/training-type.service';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';


@Component({
    selector: 'app-cpo-approved-courses',
    templateUrl: './cpo-approved-courses.component.html',
    styleUrls: ['./cpo-approved-courses.component.scss'],
    standalone: false
})
export class CPOApprovedCoursesComponent implements OnInit {
  private singleExecutionSubscription: Subscription;
  public recentError?: { error: any };

  TrainingProviderDropdown: TrainingProvider[] = [];
  selectedTrainingProvider = this.TrainingProviderDropdown;
  viewOptionsEnum = ViewOptionsEnum;
  viewType: ViewOptionsEnum = ViewOptionsEnum.LISTVIEW;
  trainingProviderControl = new FormControl();
  addressControl = new FormControl();

  filteredProviderOptions: TrainingProvider[];
  filteredAddressOptions: string[];
  courses$: Observable<TrainingProvider[]>;
  showLoadMore: boolean = false;
  totalCount: number = -1;

  trainingTypes: TrainingType[] = [new TrainingType('All', 'All', 'Tous')];
  elearning: TrainingType[] = [];
  trainingProgramOptions: DropdownOption[] = [new DropdownOption('All Safety Training Programs', 'Tous les programmes de formation en sécurité', '00000000-0000-0000-0000-000000000000')];
  sortingOptions: DropdownOption[] = [new DropdownOption('Date: Earliest - Latest', 'Date : Dernière date – première date', 'Earliest-Latest',), new DropdownOption('Date: Latest - Earliest', 'Date : Dernière date – première date', 'Latest-Earliest'), new DropdownOption('Price: Low - High', 'Prix : bas – élevé', 'Low-High'), new DropdownOption('Price: High - Low', 'Prix : élevé – bas', 'High-Low')];
  courseSearchForm = new CourseSearchForm()
  trainingTypeIdSelected: string[] = [];
  trainingSchedules: CourseSchedule[] = [];
  currentLang: string = 'en'

  today = new Date()
  year = this.today.getFullYear()
  month = this.today.getMonth()
  day = this.today.getDate()
  tpAppUtils = TrainingProgramApplicationUtils
  isELearningSelected: boolean = false;

  radiusRanges: DropdownOption[] = [
    new DropdownOption('All', 'Tous', null),
    new DropdownOption('25 kms', '25 km', 25),
    new DropdownOption('50 kms', '50 km', 50),
    new DropdownOption('100 kms', '100 km', 100)
  ]

  constructor(public translateService: TranslateService,
    @Inject('CourseService') public courseService: CourseService,
    @Inject('TrainingProviderService') public trainingProviderService: TrainingProviderService,
    @Inject('TrainingTypeService') public trainingTypeService: TrainingTypeService,
    @Inject('TrainingProgramService') public trainingProgramService: TrainingProgramService,
    @Inject('AddressService') public addressService: AddressService,
    private router: Router,
    private route: ActivatedRoute,
    private recaptchaV3Service: ReCaptchaV3Service
  ) {
    this.currentLang = translateService.currentLang;
    translateService.onLangChange.subscribe(lang => this.currentLang = lang.lang);
  }

  ngOnInit(): void {
    if (this.singleExecutionSubscription) this.singleExecutionSubscription.unsubscribe();
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    this.initSearchForm();
    this.isELearningSelected = false;

    forkJoin({
      trainingTypesData: this.trainingTypeService.GetAllTrainingTypes(this.currentLang == 'fr'),
      trainingProgramData: this.trainingProgramService.GetAllTrainingPrograms(this.currentLang == 'fr')

    }).subscribe(data => {
      this.trainingTypes = this.trainingTypes.concat(data.trainingTypesData.filter(t => t.TrainingTypeId != TrainingProgramApplicationUtils.eLearning))
      this.addToSelectedTypeId('All');
      this.elearning = this.elearning.concat(data.trainingTypesData.filter(t => t.TrainingTypeId == TrainingProgramApplicationUtils.eLearning))
      this.trainingProgramOptions = this.trainingProgramOptions.concat(data.trainingProgramData.map(x => { return new DropdownOption(x.Name, x.NameFr, x.TrainingProgramId) }))

      //prepopulate search based on training provider ID
      if (this.route.snapshot.queryParams['tpId']) {
        let tpId = this.route.snapshot.queryParams['tpId'];
        this.trainingProviderService.GetAllTrainingProvidersBySearchFragment(tpId).subscribe(data => {
          this.filteredProviderOptions = data
          this.trainingProviderControl.setValue(data[0])
          this.searchCourses();
        })
      }
      else this.searchCourses();
    });

    this.trainingProviderControl.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : value.name)),
      debounceTime(200)
    ).subscribe(name => {
      this.filteredProviderOptions = []
      if (name != null && name.length >= 1)
        this.trainingProviderService.GetAllTrainingProvidersBySearchFragment(name).subscribe(data => this.filteredProviderOptions = data);
    });

    this.addressControl.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : value.name)),
      debounceTime(200)
    ).subscribe(name => {
      this.filteredAddressOptions = []
      if (name != null && name.length >= 1)
        this.addressService.GetCityTownBySearchFragment(name).subscribe(data => this.filteredAddressOptions = data);
    });
    this.onResize();
  }

  public ngOnDestroy(): void {
    if (this.singleExecutionSubscription) this.singleExecutionSubscription.unsubscribe();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event?: any) {
    if (window.innerWidth <= 820) {
      this.onChangeViewSelection(this.viewOptionsEnum.TILEVIEW)
    } else {
      this.onChangeViewSelection(this.viewOptionsEnum.LISTVIEW)
    }
  }

  initSearchForm() {
    this.isELearningSelected = false
    const savedSortingOption = this.courseSearchForm.sortingOption;

    this.courseSearchForm = new CourseSearchForm();
    this.courseSearchForm.pageSize = 100;
    this.courseSearchForm.fromDate = this.today
    this.courseSearchForm.toDate = new Date(this.year, this.month + 3, this.day)
    this.trainingTypeIdSelected = [];
    this.addToSelectedTypeId('All');

    this.trainingProviderControl.setValue('');
    this.addressControl.setValue('');
    this.courseSearchForm.sortingOption = savedSortingOption;
  }

  addToSelectedTypeId(id: string) {
    if (id == 'All') {
      this.trainingTypeIdSelected = []
      this.isELearningSelected = false;
      this.addressControl.enable()
      this.setUnsetSelectedTypes('All');
    }

    else if (this.trainingTypeIdSelected.indexOf(id) > -1) {
      this.trainingTypeIdSelected = this.trainingTypeIdSelected.filter(x => x != id)
    } else {
      this.trainingTypeIdSelected.push(id)
      if (this.trainingTypeIdSelected.length + 1 == this.trainingTypes.length)
        this.trainingTypeIdSelected.push('All')
    }

    if (id != 'c5d83005-e948-469e-8695-bf656dd613bf' && id != 'All') {
      this.isELearningSelected = false;
      this.addressControl.enable();
      this.trainingTypeIdSelected = this.trainingTypeIdSelected.filter(x => x != 'c5d83005-e948-469e-8695-bf656dd613bf')
    }
  }

  setUnsetSelectedTypes(id: string) {
    if (id == 'All') {
      this.courseSearchForm.trainingProgramId = '00000000-0000-0000-0000-000000000000';
      this.courseSearchForm.fromDate = this.today;
      this.courseSearchForm.toDate = new Date(this.year, this.month + 3, this.day);
      this.addressControl.enable();
    }
    else {
      this.isELearningSelected = !this.isELearningSelected;
      if (this.isELearningSelected) {
        this.trainingTypeIdSelected = []

        this.courseSearchForm.trainingProgramId = TrainingProgramApplicationUtils.JHSC1;
        this.addressControl.setValue('');
        this.addressControl.disable();
        this.courseSearchForm.isWeekendsOnly = false;
        this.courseSearchForm.minSeatsAvailable = 1;
        this.courseSearchForm.fromDate = undefined;
        this.courseSearchForm.toDate = undefined;
      } else {
        this.courseSearchForm.trainingProgramId = '00000000-0000-0000-0000-000000000000';
        this.courseSearchForm.fromDate = this.today;
        this.courseSearchForm.toDate = new Date(this.year, this.month + 3, this.day);
        this.addressControl.enable();
      }
    }
  }

  displayFn(trainingProvider: TrainingProvider): string {
    return trainingProvider && trainingProvider.ApplicantBusinessOperatingName ? trainingProvider.ApplicantBusinessOperatingName : '';
  }

  searchCourses(keepData: boolean = false) {
    if (!keepData) {
      this.courseSearchForm.pageIndex = 0
      this.trainingSchedules = []
      this.totalCount = -1;
      this.showLoadMore = false;

      this.singleExecutionSubscription = this.recaptchaV3Service.execute('CpoApprovedSearch').subscribe((token) => {
        this.recentError = undefined;
        this.courseSearchForm.captchaToken = token;
        this.courseService.SearchCoursesCount(this.courseSearchForm, true).subscribe((no) => this.totalCount = no);
      }, (error) => { this.recentError = { error }; });
    }

    this.courseSearchForm.trainingTypeIdCommaSeparated = this.trainingTypeIdSelected.join(',');
    this.courseSearchForm.trainingProviderName = this.trainingProviderControl.value != null ? this.trainingProviderControl.value.ApplicantBusinessOperatingName : null
    this.courseSearchForm.cityTown = this.addressControl.value;
    this.singleExecutionSubscription = this.recaptchaV3Service.execute('CpoApprovedSearch').subscribe((token) => {
      this.recentError = undefined;
      this.courseSearchForm.captchaToken = token;
      this.courseService.SearchCourses(this.courseSearchForm, true).subscribe(data => {
        this.trainingSchedules.push(...data);
        this.showLoadMore = data.length == this.courseSearchForm.pageSize;
      })
    }, (error) => { this.recentError = { error }; });
  }

  onChangeViewSelection(selection: ViewOptionsEnum) { this.viewType = selection; }

  loadMore() {
    this.courseSearchForm.pageIndex++;
    this.searchCourses(true);
  }
}
